/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { focusBoxShadow, useTheme, VFlow } from 'bold-ui'
import { PageLoading } from 'components/loading'
import React, { useMemo } from 'react'
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion'

interface MainDetailAccordion<T extends { id: ID }, E> {
  type: string
  loading: boolean
  listOf: T[]
  evolucoes: E[]
  header: JSX.Element
  body: JSX.Element
  onChange: (uuid: string[]) => void
}

export function MainDetailAccordion<T extends { id: ID }, E>(props: MainDetailAccordion<T, E>) {
  const { type, loading, listOf, evolucoes, header, body, onChange } = props
  const { accordionItemButtonStyles, accordionItemPanelStyles, accortionItemStyles } = useStyles()

  return (
    <Accordion allowZeroExpanded={true} onChange={onChange}>
      <VFlow vSpacing={0.5}>
        {listOf.map((item: T) => (
          <AccordionItem uuid={item.id} key={item.id} css={accortionItemStyles}>
            <AccordionItemButton css={accordionItemButtonStyles}>
              {React.cloneElement(header, { ...{ [type]: item } })}
            </AccordionItemButton>
            <AccordionItemPanel css={accordionItemPanelStyles}>
              {loading ? (
                <PageLoading message='Carregando evoluções...' />
              ) : (
                evolucoes?.map((evolucao, index) => {
                  return (
                    <div
                      key={index}
                      css={css`
                        padding: 0.5rem 1rem;
                      `}
                    >
                      {React.cloneElement(body, {
                        item,
                        evolucao,
                        nextEvolucao: index + 1 < evolucoes.length ? evolucoes[index + 1] : undefined,
                      })}
                    </div>
                  )
                })
              )}
            </AccordionItemPanel>
          </AccordionItem>
        ))}
      </VFlow>
    </Accordion>
  )
}

export const useStyles = () => {
  const theme = useTheme()
  return useMemo(
    () => ({
      accortionItemStyles: css`
        border: 1px solid ${theme.pallete.gray.c60};
        border-radius: ${theme.radius.input}px;
        background: ${theme.pallete.gray.c90};
        &:focus-within {
          outline: none;
          box-shadow: ${focusBoxShadow(theme, 'primary')};
          transition-property: box-shadow;
          transition-duration: 0.2s;
        }
      `,

      accordionItemButtonStyles: css`
        padding: 0.5rem 1rem;
        cursor: pointer;
        &:hover {
          border-color: ${theme.pallete.gray.c50};
          background-color: ${theme.pallete.gray.c80};
        }
        &:focus {
          outline: none;
          box-shadow: none;
        }
        &[aria-expanded='true'] svg[name='accordionAngleIcon'] {
          transform: rotate(180deg);
          transition: 0.4s;
        }
        &[aria-expanded='false'] svg[name='accordionAngleIcon'] {
          transform: rotate(0deg);
          transition: 0.4s;
        }
      `,

      accordionItemPanelStyles: css`
        flex: 1;
        background-color: ${theme.pallete.surface.main};
        max-height: 20rem;
        overflow: auto;
        & > :not(:last-child) {
          border-bottom: 1px solid ${theme.pallete.gray.c80};
        }
      `,
    }),
    [theme]
  )
}
